import React, { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid, Modal, IconButton, List, ListItem, ListItemIcon, ListItemText} from "@mui/material";
import ResponsiveAppBar from "../components/ResponsiveAppBar";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SignUpForm from "../sections/SignupForm"; // Adjust the import path as needed
import { Close } from '@mui/icons-material';
import { Book } from "../types/book";
import { AmazonCircleFilled } from "@ant-design/icons";

const ShardSeriesBook2: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [openPreorderModal, setOpenPreorder] = useState(false);

  // Access the bookItem from the location state
  const bookItem = location.state?.bookItem as Book | undefined;

  // Fallback to URL parameter if state is not available
  const bookTitle = bookItem
    ? bookItem.title
    : (params.title ? decodeURIComponent(params.title!.replace(/-/g, " ")) : "Unknown Book");

  // Use description from the bookItem if available, otherwise provide a fallback
  const bookDescription = bookItem
    ? bookItem.description
    : "Description not available.";

  const handleNavClick = (id: string): void => {
    navigate(`/#${id}`);
  };

  const handleClose = () => {
    setOpenPreorder(false);
  }
  const handlePlatformClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  const bookPlatforms = [
    // Physical & Digital Book Stores
    { name: 'Amazon', url: 'https://www.amazon.com/dp/B0G3Q7LP2L', logo: <AmazonCircleFilled  style={{fontSize:'48px', color:'black'}}/>, color:'#FF9900' },
    // { name: 'Apple Books', url: 'https://books.apple.com', logo: <img src={appleBooksLogoUrl} alt="Apple Books" style={{width:'48px', height:'48px'}} />, color:'#FFFFFF'},
    // { name: 'Google Play Books', url: 'https://play.google.com/store/books', logo: <img src={googlePlayBooks} alt="Google Play Books" style={{width:'48px', height:'48px'}} />, color:'#4285F4' },
    // { name: 'Barnes & Noble', url: 'https://play.google.com/store/books', logo: <img src={barnesLogo} alt="Barnes & Noble" style={{width:'48px', height:'48px'}} />, color:'#366251' },
    // { name: 'Kobo', url: 'https://kobo.com', logo: <img src={koboBookLogo} alt="Kobo Books" style={{width:'48px', height:'48px'}} />, color:'#BF0000' },
  ];


  const handleBack = () => {
    // Assuming "books" is the ID of the section you want to scroll to on the home page
    // where your list of books is displayed. Adjust "books" if needed.
    handleNavClick("books");
  };
  // Mock books data (replace with actual data source)
  const books = [
    {
      title: bookTitle,
      downloadLink: bookItem?.downloadLink || "", // Adjust based on your Book type
    },
  ];
  const currentBookIndex = 0; // Adjust based on your logic
  // Basic rendering based on whether bookItem is available
  if (!bookItem && !params.title) {
      // Handle case where neither state nor URL parameter is available
      return (
          <>
            <ResponsiveAppBar onNavClick={handleNavClick} />
            {/* Add background to this container as well for consistency or if this page is standalone */}
            <Container
              maxWidth="lg"
              sx={{
                mt: 10,
                mb: 4,
                py: 4, // Add vertical padding to the container itself
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                // Optional: Add a fallback background color
                backgroundColor: '#f0f0f0', // Example light grey fallback
              }}
            >
                <Paper elevation={3} sx={{ p: 4, mb: 4, textAlign: 'center' }}> {/* Added textAlign center for the error message */}
                    <Typography variant="h6" gutterBottom>Book Not Found</Typography> {/* Added gutterBottom */}
                    <Typography variant="body1">
                        The details for this book could not be loaded. Please try again from the book list.
                    </Typography>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBack}
                        sx={{ mt: 3 }} // Increased top margin
                      >
                        Back to All Books
                      </Button>
                </Paper>
            </Container>
          </>
      );
  }

  // Main render when book details are available
  return (
    <>
      <ResponsiveAppBar onNavClick={handleNavClick} />
      <Container
        maxWidth="lg"
        sx={{
          mt: 10,
          mb: 4,
          py: 4, // Add vertical padding to the container itself
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          // Optional: Add a fallback background color
          backgroundColor: '#00000000', // Example light grey fallback
        }}
      >
        <Paper
          elevation={6} // Increased elevation for better separation
          sx={{
            p: { xs: 3, md: 4 }, // Add padding inside the paper
            borderRadius: '8px', // Optional: add rounded corners
            // --- Styles for semi-transparency and blur ---
            backgroundColor: 'rgba(255, 255, 255, 0.3)', // Semi-transparent white (adjust alpha)
            backdropFilter: 'blur(15px)', // Apply blur to the background behind the paper
            WebkitBackdropFilter: 'blur(15px)', // For Safari support
            boxShadow: '0px 10px 30px rgba(0,0,0,0.15)', // Adjusted shadow
            overflow: 'hidden', // Helps contain blur effect if needed
          }}
        >
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', alignItems: 'center' }}> {/* Fixed: 'size' to 'item' with correct props */}
              {/* Display cover image */}
              <Box
                sx={{
                  width: "100%",
                  // Maintain aspect ratio or set a specific height
                  height: { xs: 300, md: 350 }, // Responsive height
                  bgcolor: "#00000000", // Placeholder background color
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  verticalAlign: "middle",
                  borderRadius: '4px', // Optional: rounded corners for image box
                  overflow: 'hidden', // Ensure image respects border radius
                  // Example: Display cover image if available in bookItem
                  backgroundImage: bookItem?.coverImageUrl ? `url(${bookItem.coverImageUrl})` : 'none',
                  backgroundSize: 'contain', // Cover the entire box
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                }}
              >
                {!bookItem?.coverImageUrl && <Typography variant="body1" color="text.secondary">Book Cover Placeholder</Typography>}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}> {/* Fixed: 'size' to 'item' with correct props */}
              <Typography
                variant="h2" // Increased from h3 to h2
                component="h1"
                gutterBottom
              >
                {bookTitle}
              </Typography>
              <Typography
                variant="h5" // Increased from subtitle1 to h6
                color="text.secondary"
                gutterBottom
              >
                By Martin Knaggs
              </Typography>
              <Box sx={{ my: 3 }}>
                <Typography variant="h4" gutterBottom>
                  About this book
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ 
                    lineHeight: 1.7, 
                    whiteSpace: "pre-wrap", 
                    fontSize: "1.2rem",
                    textAlign: "left",  // Explicitly set left alignment
                    margin: 0,          // Remove any default margins
                    padding: 0          // Remove any default padding
                  }}
                >
                  {bookDescription.trim()}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  mt: 2,
                }}
              >
                <Button
                  variant="contained"
                  sx={{
                    marginRight: { xs: 0, sm: '5%' },
                    padding: { xs: '15px 30px', md: '20px 48px' },
                    fontSize: { xs: '1.2rem', md: '1.5rem' },
                    width:'100%',
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid #55DEF1',
                    color: '#000000ff',
                    '&:hover': {
                      background: 'rgba(85, 222, 241, 0.2)',
                      backdropFilter: 'blur(15px)',
                      border: '2px solid #55DEF1',
                    }
                  }}
                  startIcon={<ArrowBackIcon />}
                  size="large"
                  onClick={handleBack}
                >
                  Back to All Books
                </Button>
                {books[currentBookIndex].downloadLink && (
                  <Button
                    variant="contained"
                    sx={{
                      marginRight: { xs: 0, sm: '5%' },
                      padding: { xs: '15px 30px', md: '20px 48px' },
                      fontSize: { xs: '1.2rem', md: '1.5rem' },
                      width:'100%',
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid #55DEF1',
                      color: '#000000ff',
                      '&:hover': {
                        background: 'rgba(85, 222, 241, 0.2)',
                        backdropFilter: 'blur(15px)',
                        border: '2px solid #55DEF1',
                      }
                    }}
                    size="large"
                    onClick={()=> {
                      setOpenPreorder(true);
                    }}
                  >
                    PRE-ORDER
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
        <Box sx={{ mt: { xs: 4, md: 6 } }}> 
            <SignUpForm/>
        </Box>
        <Modal
              open={openPreorderModal}
              onClose={handleClose}
              aria-labelledby="book-platforms-modal-title"
            >
              <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 500,
                maxHeight: '80vh',
                backgroundColor: '#ffffffff',
                color:"black",
                borderRadius: 2,
                boxShadow: 24,
                p: 3,
                overflow: 'auto',
              }}>
                <IconButton
                  aria-label="close"
                  onClick={handleClose}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  <Close />
                </IconButton>
                
                <Typography id="book-platforms-modal-title" variant="h5" component="h2" sx={{ mb: 3, pr: 4 }}>
                  Get the Book
                </Typography>
                
                {/* Book Stores Section */}
                <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                  Buy Physical & Digital Books
                </Typography>
                <List dense>
                  {bookPlatforms.map((platform) => (
                    <ListItem
                      key={platform.name}
                      component="div"
                      onClick={() => handlePlatformClick(platform.url)}
                      sx={{
                        borderRadius: 3, // Increased for more rounded corners
                        cursor: 'pointer',
                        padding: 2.5, // Increased padding for better spacing
                        margin: 1, // Add margin between items
                        background: `linear-gradient(135deg, ${platform.color} 0%, transparent 70%)`,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          transform: 'translateY(-2px)', // Subtle lift effect on hover
                          boxShadow: 2, // Add shadow on hover
                        },
                        transition: 'all 0.2s ease-in-out', // Smooth transitions
                      }}
                    >
                      <ListItemIcon sx={{ 
                        minWidth: 56, // Increased spacing from icon
                        marginRight: 2 // Additional right margin
                      }}>
                        {platform.logo}
                      </ListItemIcon>
                      <ListItemText
                        primary={platform.name}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontWeight: 600, // Bolder text
                            fontSize: '1.25rem', // Larger font size (20px)
                            letterSpacing: '0.02em', // Slight letter spacing for readability
                            color: 'text.primary', // Ensure good contrast
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
        </Modal>
      </Container>
    </>
  );
};

export default ShardSeriesBook2;
